import React, { useState, useMemo } from "react"
import { useHits } from "react-instantsearch"
import { Table, Column } from "fixed-data-table-2"
import { AutoSizer } from "react-virtualized"
import Pagination from "./pagination"
import Checkboxes from "./checkboxes"
import Add from "./add"
import { breakpoints } from "../../utils"

const ATTRIBUTES = [
    { label: "Installation", value: "ostendoInstallationMethod", width: 120 },
    { label: "Trim Colour", value: "ostendoTrimColour", width: 110 },
    { label: "Beam", value: "ostendoBeamAngle", hideZeros: true, width: 100 },
    { label: "CCT", value: "ostendoColourTemperature", hideZeros: true, width: 110 },
    { label: "Lumens", value: "ostendoLumenOutput", unit: "lm", hideZeros: true, width: 110 },
    { label: "Power", value: "ostendoSystemPower", unit: "W", hideZeros: true, width: 100 },
    { label: "Dimming", value: "ostendoDimmingOption", width: 150 },
    { label: "Access.", value: "ostendoAccessories", width: 120 },
]

const checkAttributes = (hits, attrValue) =>
    hits.filter(hit => hit[attrValue] !== 0 && typeof hit[attrValue] !== "undefined").length

const addToStorage = (product, generic = false) => {
    const enquiryProduct = {
        title: product.title,
        description: generic ? window.productDescription : product.ostendoItemDescription,
        image: product.ostendoProductImages?.[0]?.crop210x210 || "",
        id: generic ? window.productId : product.objectID,
        code: generic ? product.ostendoItemCode?.slice(0, 4) : product.ostendoItemCode,
        ostendoAccessories: generic ? product.ostendoItemCode?.slice(0, 4) : product.ostendoAccessories,
        pdf: true,
        count: "1",
    }

    const storage = []
    const local = localStorage.getItem("enquiry-product")
    if (local) {
        JSON.parse(local).forEach(item => {
            if (item.id !== enquiryProduct.id) storage.push(item)
        })
    }

    if (!local || generic || (local && !local.includes(enquiryProduct.id))) {
        storage.push(enquiryProduct)
        window.dataLayer?.push({
            event: "add-product",
            productName: enquiryProduct.title,
            productCode: enquiryProduct.code,
        })
    }

    if (storage.length) {
        localStorage.setItem("enquiry-product", JSON.stringify(storage))
    } else {
        localStorage.removeItem("enquiry-product")
    }

    window.dispatchEvent(new Event("on-storage"))
}

const List = ({ modelName, modelCode }) => {
    var { items } = useHits()
    const [currentPage, setCurrentPage] = useState(1)
    const hitsPerPage = 7
    const fixedColumn = window.matchMedia(breakpoints.portable).matches
    const paginatedHits = useMemo(() => {
        const start = (currentPage - 1) * hitsPerPage
        return items.slice(start, start + hitsPerPage)
    }, [items, currentPage])

    const sortedHits = items
	const indexOfLastHit = currentPage * hitsPerPage
	const indexOfFirstHit = indexOfLastHit - hitsPerPage

    const numHits = items.length
    items = sortedHits.slice(indexOfFirstHit, indexOfLastHit)

    if (numHits === 0) return null
    return (
        <div className="configurator__list">
            <div className="table intro-click-marker">
                <AutoSizer disableHeight>
                    {({ width }) => (
                        <Table
                            rowsCount={numHits >= hitsPerPage ? hitsPerPage : numHits}
                            rowHeight={70}
                            headerHeight={70}
                            width={width}
                            height={numHits >= hitsPerPage ? 588 : (numHits * 75) + 75}
                            touchScrollEnabled
                        >
                            {ATTRIBUTES.map(attr => (
                                checkAttributes(items, attr.value) > 0 && (
                                    <Column
                                        key={attr.value}
                                        flexGrow={1}
                                        width={attr.width}
                                        header={
                                            <div className="table__cell table__cell--header">
                                                <Checkboxes
                                                    heading={attr.label}
                                                    attributeName={attr.value}
                                                    modelName={modelName}
                                                    modelCode={modelCode}
                                                    dropdown={attr.label}
                                                    withExpander
                                                />
                                            </div>
                                        }
                                        cell={({ rowIndex }) => {
                                            const hit = items[rowIndex]
                                            if (attr.value === "ostendoAccessories") {
                                                return (
                                                    <div className={`${attr.value} table__cell`}>
                                                        {hit?.ostendoAccessories || ""}
                                                    </div>
                                                )
                                            }
                                            const val = hit?.[attr.value]
                                            return (
                                                <div className={`${attr.value} table__cell`}>
                                                    {(val === 0 && attr.hideZeros) ? "" : `${val || ""}${attr.unit || ""}`}
                                                </div>
                                            )
                                        }}
                                    />
                                )
                            ))}

                            <Column
                                fixed={fixedColumn}
                                width={200}
                                flexGrow={1}
                                header={
                                    <div className="table__cell table__cell--header table__cell--highlight">
                                        Product Code
                                    </div>
                                }
                                cell={({ rowIndex }) => {
                                    const hit = items[rowIndex]
                                    return (
                                        <div className="table__cell table__cell--highlight">
                                            {hit?.ostendoItemCode}
                                            <div className="btn__wrap">
                                                <a
                                                    href={`${window.location.origin}/products/pdf/${hit?.objectID}`}
                                                    className="btn btn--tooltip gtm-product-download"
                                                    data-product-name={hit?.title}
                                                    data-product-code={hit?.ostendoItemCode}
                                                    target="_blank"
                                                >
                                                    <svg><use xlinkHref="#datasheet-icon" /></svg>
                                                    <div className="btn__tooltip">
                                                        Download datasheet
                                                    </div>
                                                </a>
                                                <Add
                                                    id={hit?.objectID}
                                                    addToStorage={() => addToStorage(hit, false)}
                                                />
                                            </div>
                                        </div>
                                    )
                                }}
                            />
                        </Table>
                    )}
                </AutoSizer>
            </div>
            <Pagination
                hitsPerPage={hitsPerPage}
                numHits={numHits}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                addGenericProduct={() => addToStorage(items[0], true)}
            />
        </div>
    )
}
export default List